import { Component, HostListener, OnInit, inject } from '@angular/core';
import { AuthService } from './route/auth/auth.service';
import { SplitViewObserverService } from './shared/components/split-view/split-view-observer.service';
import { AppConfig } from './appconfig';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { filter, switchMap, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from './shared/dialogs/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from './shared/dialogs/error-dialog/error-dialog.component';
import { RouterOutlet } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

@Component({
  selector: 'lfw-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  imports: [RouterOutlet]
})
export class AppComponent implements OnInit {
  @HostListener('window:resize', ['$event'])
  notifyMoved(event: any) {
    this.splitViewObserver.notifyMoved({ gutterNumber: 0 });
  }

  @HostListener('beforeinstallprompt', ['$event'])
  promtPWAInstall(event: BeforeInstallPromptEvent) {
    event.preventDefault();
    if (this.appConfig.isMobileApp()) {
      console.info('remove manifest (either mobile app or enablepwa is false ');
      document.querySelector('link[rel="manifest"]')?.remove();
      return;
    }
    event.userChoice.then((choice) => {
      if (choice.outcome === 'dismissed') {
        console.info('user denied installing pwa');
      }
    });
    event.prompt();
  }

  private authService = inject(AuthService);
  private splitViewObserver = inject(SplitViewObserverService);
  private swUpdate = inject(SwUpdate);
  private dialog = inject(MatDialog);
  private appConfig = inject(AppConfig);

  ngOnInit(): void {
    this.authService.runInitialLoginSequence().then(() => {
      this.initializeServiceWorker();
    });

    if (this.appConfig.isMobileApp()) {
      console.info('removing manifest ... ');
      document.querySelector('link[rel="manifest"]')?.remove();
    }
  }

  initializeServiceWorker() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.unrecoverable
        .pipe(
          takeUntilDestroyed(),
          switchMap(() =>
            this.dialog
              .open(ErrorDialogComponent, {
                data: {
                  title: 'Update fehlgeschlagen',
                  message: 'Die Seite wird neu geladen.'
                }
              })
              .afterClosed()
          )
        )
        .subscribe(async () => {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.unregister();
          }
          location.reload();
        });

      this.swUpdate.versionUpdates
        .pipe(
          takeUntilDestroyed(),
          filter((evt: VersionEvent): evt is VersionReadyEvent => evt.type === 'VERSION_READY'),
          switchMap((evt: VersionReadyEvent) => {
            console.info(
              `currentVersion=[${JSON.stringify(evt.currentVersion)} | latestVersion=[${JSON.stringify(
                evt.latestVersion
              )}]`
            );
            return this.dialog
              .open(ConfirmDialogComponent, {
                disableClose: true,
                closeOnNavigation: false,
                data: {
                  title: 'Neue Version der Anwendung verfügbar',
                  message2: 'soll die neue Version geladen werden?',
                  isOkDefault: true
                }
              })
              .afterClosed();
          }),
          tap((result) => {
            if (result) {
              window.location.reload();
            }
          })
        )
        .subscribe();
    }
  }
}
